import { useParams } from 'react-router-dom';
import { useState } from 'react';
import { useI18n } from '@/hooks/useI18n';
import { useStripeReturnFeedback } from '@/hooks/useStripeReturnFeedback';
import { useSharedProposal } from '@/lib/shared-proposal/hooks/useSharedProposal';
import { PasswordGate } from '@/lib/shared-proposal/components/PasswordGate';
import { SharedProposalView } from '@/lib/shared-proposal/components/SharedProposalView';
import { AlertCircle, Clock } from 'lucide-react';

export const SharedProposalPage = () => {
  const { shareToken } = useParams<{ shareToken: string }>();
  const { fetchProposal, isLoading, error, data } = useSharedProposal(shareToken!);
  const { t } = useI18n();
  const [usedPassword, setUsedPassword] = useState<string | undefined>();

  // Rientro da Stripe Checkout. Qui non si puo' ricaricare la proposta da soli: se il
  // link ha una password, il cliente la reinserisce e il gate ricompare comunque. Il
  // toast e' l'unico modo che ha di sapere che il pagamento e' partito.
  useStripeReturnFeedback();

  console.log('[SharedProposalPage] Mounted with shareToken:', shareToken);
  console.log('[SharedProposalPage] Current state:', { isLoading, error, hasData: !!data });

  // Non chiamiamo fetchProposal automaticamente - aspettiamo che l'utente inserisca la password

  // Handler per la submission della password
  const handlePasswordSubmit = async (pwd: string) => {
    setUsedPassword(pwd);
    await fetchProposal(pwd);
  };

  // Se non abbiamo ancora dati, mostra sempre il gate password
  if (!data) {
    console.log('[SharedProposalPage] No data yet, showing password gate. Current error:', error);
    // Link expired
    if (error === 'link_expired') {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md w-full p-8 bg-white rounded-lg shadow-lg text-center">
            <Clock className="h-12 w-12 mx-auto mb-4 text-orange-500" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {t('sharedProposal.linkExpired')}
            </h1>
            <p className="text-gray-600">
              {t('sharedProposal.linkExpiredDesc')}
            </p>
          </div>
        </div>
      );
    }

    // Link not found
    if (error === 'link_not_found') {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md w-full p-8 bg-white rounded-lg shadow-lg text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-red-500" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {t('sharedProposal.linkNotFound')}
            </h1>
            <p className="text-gray-600">
              {t('sharedProposal.linkNotFoundDesc')}
            </p>
          </div>
        </div>
      );
    }

    // Mostra il gate password (può avere errore invalid_password o nessun errore)
    return (
      <PasswordGate
        onSubmit={handlePasswordSubmit}
        error={error || undefined}
        isLoading={isLoading}
      />
    );
  }

  // Abbiamo i dati - mostra la proposta
  if (data?.success && data.itinerary && data.sharedBy) {
    console.log('[SharedProposalPage] Rendering view with data:', {
      paymentEnabled: data.payment_enabled,
      totalPrice: data.itinerary.total,
      hasItinerary: !!data.itinerary,
      hasSharedBy: !!data.sharedBy
    });
    
    return (
      <SharedProposalView
        itinerary={data.itinerary}
        sharedBy={data.sharedBy}
        paymentEnabled={data.payment_enabled}
        paymentPlan={data.payment_plan ?? null}
        password={usedPassword}
      />
    );
  }

  // Fallback
  return null;
};

export default SharedProposalPage;
